/**
 * 分享功能工具函數庫
 * 清理版：移除從未被調用的 generateImageFromHTML、generateStructuredData 死代碼
 */

/**
 * 格式化日期供分享使用
 * @param {Date} date
 * @param {string} language - 'zh' | 'en'
 * @returns {string}
 */
export const formatDateForSharing = (date, language) => {
  if (!date) return '';
  if (language === 'zh') {
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
  } else {
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }
};

/**
 * 生成各平台分享文字
 * @param {Object} params
 * @returns {Object} { base, social, email, sms }
 */
export const generatePlatformSpecificText = ({ language, calculatedDays, relationshipLabel, detailedTime }) => {
  const years  = detailedTime?.years  ? `${detailedTime.years}  ${language === 'zh' ? '年' : 'years'} `  : '';
  const months = detailedTime?.months ? `${detailedTime.months} ${language === 'zh' ? '月' : 'months'} ` : '';
  const days   = detailedTime?.days   ? `${detailedTime.days}   ${language === 'zh' ? '日' : 'days'}`   : '';

  const detailedTimeText = (years || months || days) ? ` (${years}${months}${days})` : '';

  const baseText = language === 'zh'
    ? `我們${relationshipLabel}的日子已經 ${calculatedDays} 天了${detailedTimeText}！💕`
    : `We've been ${relationshipLabel} for ${calculatedDays} days${detailedTimeText}! 💕`;

  const socialText = `${baseText} ${language === 'zh' ? '#愛情時光 #紀念日 #愛情里程碑' : '#LoveJourney #Anniversary #LoveMilestones'}`;

  return { base: baseText, social: socialText, email: baseText, sms: baseText };
};

/**
 * 檢查瀏覽器是否支援 Web Share API
 * @returns {boolean}
 */
export const isWebShareSupported = () => {
  return typeof navigator !== 'undefined' && !!navigator.share;
};

/**
 * 從 data URL 觸發下載
 * @param {string} dataUrl
 * @param {string} fileName
 */
export const downloadImage = (dataUrl, fileName = 'love-journey.png') => {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * 生成 SEO 友好的分享 URL
 * @param {Object} params
 * @returns {string}
 */
export const generateSEOFriendlyURL = ({ anniversaryDate, anniversaryType, language }) => {
  if (!anniversaryDate) return window.location.href;

  const baseUrl = window.location.origin + window.location.pathname;
  const dateStr = [
    anniversaryDate.getFullYear(),
    String(anniversaryDate.getMonth() + 1).padStart(2, '0'),
    String(anniversaryDate.getDate()).padStart(2, '0')
  ].join('-');

  const url = new URL(baseUrl);
  url.searchParams.set('date', dateStr);
  url.searchParams.set('type', anniversaryType || 'couple');
  url.searchParams.set('lang', language || 'en');
  return url.toString();
};

/**
 * 解析 URL 參數（date / type / lang）
 * @returns {{ date: Date|null, type: string|null, language: string|null }}
 */
export const parseUrlParams = () => {
  const params = new URLSearchParams(window.location.search);
  const result = { date: null, type: null, language: null };

  const dateParam = params.get('date');
  if (dateParam) {
    const [year, month, day] = dateParam.split('-').map(Number);
    if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
      result.date = new Date(year, month - 1, day);
    }
  }

  const typeParam = params.get('type');
  const validTypes = ['couple', 'wedding', 'first-meet', 'first-date', 'proposal', 'custom'];
  if (typeParam && validTypes.includes(typeParam)) {
    result.type = typeParam;
  }

  const langParam = params.get('lang');
  if (langParam && ['zh', 'en'].includes(langParam)) {
    result.language = langParam;
  }

  return result;
};